export type PendingCapture = {
  token: string;
  kind: "PHOTO" | "VIDEO";
  mime: string;
  blob: Blob;
  lat: number;
  lng: number;
  accuracyM: number;
  capturedAt: number;
  note: string;
  slotIndex: number;
};

export type PendingCaptureSet = {
  token: string;
  items: PendingCapture[];
};

const DB_NAME = "hkp-product-capture";
const STORE = "pending";
const SLOT_COUNT = 4;

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "token" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not open local capture storage"));
  });

const asSet = (raw: any, token: string): PendingCaptureSet => {
  if (raw && Array.isArray(raw.items)) {
    return {
      token,
      items: raw.items.filter((item: PendingCapture) => item && item.blob),
    };
  }
  if (raw && raw.blob) {
    return {
      token,
      items: [{ ...raw, slotIndex: 0, token }],
    };
  }
  return { token, items: [] };
};

export const loadPendingSet = async (token: string): Promise<PendingCaptureSet> => {
  const db = await openDb();
  const raw = await new Promise<any>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(token);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not read saved takes"));
  });
  db.close();
  return asSet(raw, token);
};

export const savePendingSet = async (pending: PendingCaptureSet): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Could not save these takes on the phone"));
    tx.objectStore(STORE).put({
      token: pending.token,
      items: pending.items.slice(0, SLOT_COUNT),
    });
  });
  db.close();
};

export const clearPendingCapture = async (token: string): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Could not clear saved takes"));
    tx.objectStore(STORE).delete(token);
  });
  db.close();
};

export { SLOT_COUNT };
